/**
 * @author: lv.chen
 * @date: 2025/12/3
 * @desc: DateSelect组件
 */
import React, { useMemo } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import RangePicker from '@home/components/RangePicker';
import { componentDefault } from '@intelligence/components/Filter/FilterBase/FilterList';
import { formatDateStr } from '@intelligence/v3/utils/date';
import Bubble from '@home/components/TeaComponents/Bubble';

import './index.scss';

const LANGUAGE = 'en';
const DATE_TYPE = 'date';
const DATE_TYPES = [DATE_TYPE];
const AUTO_RANGE = {
  timeType: DATE_TYPE,
  length: 0,
};

interface IDateSelectProps extends IBaseProps {
  // 有数据的日期
  availableDates: string[];
  value: string;
  onChange: (date: string) => void;
}

const DateSelect: React.FC<IDateSelectProps> = (props) => {
  const { style, className, availableDates, value: valueProps, onChange } = props;

  // 符合RangePicker组件的禁用日期数据
  const disabledDate = useMemo<IRecord<(m: moment.Moment) => boolean>>(() => {
    return {
      [DATE_TYPE]: (date: moment.Moment) => {
        if (!moment.isMoment(date) || !date.isValid()) {
          return false;
        }

        if (!Array.isArray(availableDates) || !availableDates.length) {
          return true;
        }

        return availableDates.includes(date.format('YYYY-MM-DD'));
      },
    };
  }, [availableDates]);

  // 符合RangePicker组件的value
  const value = useMemo<[moment.Moment, moment.Moment] | ['', '']>(() => {
    if (!valueProps) return ['', ''];
    return [moment(valueProps), moment(valueProps)];
  }, [valueProps]);

  // RangePicker组件的change回调
  const handleChange = (value: any, type: string) => {
    let dateValue = ['', ''];

    if (Array.isArray(value) && value.length === 2) {
      const startDate = value[0];
      let endDate = value[1];
      if (startDate === '' && endDate === '') {
        dateValue = componentDefault?.[type];
      } else {
        if (type === 'year') {
          endDate = moment(endDate).endOf('year');
        }
        if (type === 'month') {
          endDate = moment(endDate).endOf('month');
        }
        dateValue = [startDate, endDate];
      }
    }

    onChange?.(formatDateStr(dateValue?.[0]));
  };

  return (
    <div style={style} className={classnames('dbi-casual-daily-reports-date-select', className)}>
      <RangePicker
        className=""
        language={LANGUAGE}
        showSelectType={false}
        showShortcut={false}
        syncInput={false}
        autoRange={AUTO_RANGE}
        startOfWeek={1}
        type={DATE_TYPE}
        typeList={DATE_TYPES}
        disabledDate={disabledDate}
        defaultValue={componentDefault}
        value={value}
        onChange={handleChange}
        label={
          <div>
            <Bubble content="点击筛选查看历史日报">
              <div className="dbi-topic-date-wrap">
                <div className="dbi-topic-date-icon"></div>
                <div className="dbi-topic-date-text">{valueProps}</div>
              </div>
            </Bubble>
          </div>
        }
      />
    </div>
  );
};

export default DateSelect;
